/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react'
import { userSelector } from 'ducks/user'
import { useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import { MainLayout } from 'views/layouts'
import {
  CompanyCard,
  IconSvgProfile,
  Row,
  CardContainer,
  Card,
  CardDescriptionSection,
  LogoContainer,
  Circle,
  ErrorContainer,
  TextError,
  InfoContainer,
  CheckboxContainer,
  LastButtonsContainer,
  InfoWrapper,
  ButtonEdit,
} from './styled'
import { H2, H3, H4, P } from 'views/components/UI/Text'
import { superAdminRoutes } from 'router/routes'
import getOneCompany from 'services/companies/getOneCompany'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { Company as CompanyType } from 'types/common'
import getIntegrations, { IIntegration } from 'services/companies/getIntegrations/getIntegrations'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Block } from 'views/components/UI/Contents'
import Input from 'views/components/UI/TextInput'
import Select from 'views/components/UI/Select'
import { Switch } from '@mui/material'
import RadioCheck from 'views/components/UI/InputRadio'
import { generalDataSelector } from 'ducks/auth'
import { Button, StrokedButton } from 'views/components/UI/Buttons'
import LoopIcon from '@mui/icons-material/Loop'
import { MAX_FILE_SIZE_LOGO } from 'constants/global.constants'
import { useNotification } from 'lib/context/notification.context'
import editCompany from 'services/companies/editCompany'
import TooltipComponent from 'views/components/Tooltip'

const SignUpSchema = Yup.object().shape({
  name: Yup.string().required('El nombre de la empresa es obligatorio'),
  operativeId: Yup.string().required('El nombre del operativo es obligatorio'),
  contactName: Yup.string().required('El nombre del contacto es obligatorio'),
  contactEmail: Yup.string().email('El email no es válido').required('El email es obligatorio'),
  phone: Yup.string()
    .required('El teléfono es obligatorio')
    .min(7, 'El teléfono debe tener al menos 7 caracteres')
    .max(16, 'El teléfono debe tener máximo 16 caracteres'),
  commercialInCharge: Yup.string().required('El comercial encargado es obligatorio'),
  companyCategory: Yup.string().required('La categoría de la empresa es obligatoria'),
  typeCompanyId: Yup.string().required('El carácter es obligatorio'),
  industryType: Yup.string().required('El tipo de industria es obligatorio'),
  hasIntegration: Yup.string().required('La integración es obligatoria'),
  integrationType: Yup.number().when('hasIntegration', {
    is: (value: boolean | string) => value === true || value === 'true',
    then: Yup.number().required('El tipo de integración es obligatorio'),
  }),
})

const MoreInfoSchema = Yup.object().shape({
  workModel: Yup.string().required('El modelo de trabajo es obligatorio').nullable(),
  marketComplexity: Yup.string().required('La complejidad del mercado es obligatoria').nullable(),
  lifeCycle: Yup.string().required('El ciclo de vida de la empresa es obligatorio').nullable(),
  maturityLevel: Yup.string().required('La madurez del área de talento humano es obligatoria').nullable(),
})

type ISubmit = {
  name: string
  typeCompanyId: number
  operativeId: number
  contactName: string
  contactEmail: string
  phone: string
  companyCharacter: string
  logo?: Blob
  hasIntegration: string
  integrationType?: number
  commercialInCharge: string
  companyCategory: number
  industryType: string
  usesAi: boolean
}

const Company: FC = () => {
  const navigate = useNavigate()
  const { getError, getSuccess, getWarning } = useNotification()

  const { userId, token } = useSelector(userSelector)
  const { data } = useSelector(generalDataSelector)

  const [params, setParams] = useState<any>(null)
  const [idCurrentCompany, setIdCurrentCompany] = useState<any>(null)
  const [, setEditUrl] = useState<any>(null)

  const [companyInfo, setCompanyInfo] = useState<CompanyType>({} as CompanyType)
  const [imageCompany, setImageCompany] = useState<string>('')
  const [newImageBlob, setNewImageBlob] = useState<Blob | null>(null)
  const [companyCharacter, setCompanyCharacter] = useState<string | undefined>()
  const [companyCategory, setCompanyCategory] = useState<string | undefined>()
  const [, setHasIntegration] = useState<string>('false')
  const [integrations, setIntegrations] = useState<IIntegration[]>([])
  const [loading, setLoading] = useState(false)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [isEditing, setIsEditing] = useState(false)
  const [addMoreInfo, setAddMoreInfo] = useState(false)
  const [aiActive, setAiActive] = useState(false)
  const [refresh, setRefresh] = useState(false)

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    getIntegrations().then((res) => {
      if (res.status === 'ok') {
        setIntegrations(res.data)
      } else {
        console.error('error on get integrations')
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const associatedOperating =
    data?.list_admin &&
    data?.list_admin?.map((admin: any) => {
      return {
        value: admin.id,
        label: `${admin.first_name} ${admin.last_name}`,
      }
    })

  const industryTypes =
    data?.company_industry_type &&
    data?.company_industry_type?.map((type: any) => {
      return {
        value: type.id,
        label: type.name,
      }
    })

  const maturityLevels = data?.company_hr_maturity_level.map((level: any) => {
    return {
      value: level.id,
      label: level.name,
    }
  })

  const lifeCycles = data?.company_life_cycle.map((cycle: any) => {
    return {
      value: cycle.id,
      label: cycle.name,
    }
  })

  const marketComplexity = data?.company_market_complexity.map((complexity: any) => {
    return {
      value: complexity.id,
      label: complexity.name,
    }
  })

  function getCompanyInfo() {
    setLoading(true)
    getOneCompany(idCurrentCompany)
      .then((response: any) => {
        setLoading(false)
        setCompanyInfo(response.data)
        setCompanyCategory(response.data.categoryName)
        setCompanyCharacter(response.data.character)
        setImageCompany(response.data.logo)
      })
      .catch((error) => {
        setLoading(false)
        console.error('Error fetching company info:', error)
        getError('No fue posible obtener la información de la compañia')
      })
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    setParams(Object.fromEntries(searchParams))
  }, [])

  useEffect(() => {
    if (params) {
      setIdCurrentCompany(parseInt(params.companyId))
      setEditUrl(`/companies/update?companyId=${params.companyId}`)
      if (params.edit) {
        setIsEditing(true)
      }
    }
  }, [params])

  // Get company info
  useEffect(() => {
    if (idCurrentCompany && token && userId) {
      getCompanyInfo()
    }
  }, [idCurrentCompany, token, userId, refresh])

  const onSubmit = (values: ISubmit) => {
    if (values.usesAi) {
      if (
        companyInfo.workModel === null &&
        companyInfo.marketComplexity === null &&
        companyInfo.lifeCycle === null &&
        companyInfo.hrMaturityLevel === null
      ) {
        getWarning('Debes agregar las características organizacionales para continuar')
        return
      }
    }
    if (newImageBlob !== null) {
      values.logo = newImageBlob
    }

    if (values.hasIntegration === 'false') {
      values.integrationType = undefined
    }

    if (idCurrentCompany) {
      const dataCompany = {
        ...values,
      }

      editCompany(dataCompany, companyInfo.id)
        .then((res) => {
          if (res.status === 'OK' || res.status === 'success') {
            getSuccess('Compañia modificada')
            navigate('/companies')
          } else {
            console.error('error editing company ')
            getError('No fue posible modificar la compañia')
          }
        })
        .catch((err) => {
          console.error('error editing company ', err)
        })
    }
  }

  const isDisabled = (allErrors: any) => {
    return Object.keys(allErrors).length > 0
  }

  const handleEdit = () => {
    setIsEditing(!isEditing)
  }

  const handleAddMoreInfo = () => {
    setAddMoreInfo(true)
  }

  const handleImage = (e: any) => {
    const file = e.target.files[0]

    if (file) {
      if (file.size > MAX_FILE_SIZE_LOGO) {
        getError('El tamaño del logo no debe exceder los 600 kilobytes.')
        return
      } else {
        setNewImageBlob(file)

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          setCompanyInfo({ ...companyInfo, logo: reader.result as string })
        }
      }
    }
  }

  const validateMoreInfo = () => {
    if (
      (companyInfo.lifeCycle === null ||
        companyInfo.hrMaturityLevel === null ||
        companyInfo.workModel === null ||
        companyInfo.marketComplexity === null) &&
      aiActive
    ) {
      return true
    } else {
      return false
    }
  }

  const handleSubmitAddMoreInfo = (values: any) => {
    if (idCurrentCompany) {
      const dataCompany = {
        ...values,
      }

      editCompany(dataCompany, companyInfo.id)
        .then((res) => {
          if (res.status === 'OK' || res.status === 'success') {
            getSuccess('Compañía modificada')
            setAddMoreInfo(false)
            setRefresh(!refresh)
          } else {
            console.error('error editing company ')
            getError('No fue posible modificar la compañia')
          }
        })
        .catch((err) => {
          console.error('error editing company ', err)
        })
    }
  }

  // MARK: RENDER

  return (
    <MainLayout>
      <div style={{ paddingBottom: 40 }}>
        {companyInfo.id ? (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 35,
                alignItems: 'center',
              }}
            >
              <h2 style={{ fontSize: 30 }}>Información - {companyInfo.name}</h2>
            </div>
            <CardContainer style={{ paddingBottom: 20 }}>
              <Card>
                <Link
                  to={superAdminRoutes.Inventory.replace(':companyId', companyInfo.id.toString())}
                  style={{ width: '100%', height: '100%' }}
                >
                  <CardDescriptionSection>
                    <span style={{ fontWeight: 'bold', color: '#1F2124', fontSize: 20 }}>Inventarios</span>
                  </CardDescriptionSection>
                </Link>
              </Card>
              <Card>
                <Link
                  to="/company/admin-platform-per-company"
                  state={{ companyId: idCurrentCompany }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <CardDescriptionSection>
                    <span style={{ fontWeight: 'bold', color: '#1F2124', fontSize: 20 }}>
                      Usuarios administradores de plataforma
                    </span>
                  </CardDescriptionSection>
                </Link>
              </Card>
              <Card
                onClick={() => {
                  navigate(superAdminRoutes.EvaluatedUserByCompany, {
                    state: { companyId: companyInfo.id, companyName: companyInfo.name },
                  })
                }}
              >
                <CardDescriptionSection>
                  <span style={{ fontWeight: 'bold', color: '#1F2124', fontSize: 20 }}>Usuarios evaluados</span>
                </CardDescriptionSection>
              </Card>
              <Card>
                <Link
                  to="/company/homologation-skills"
                  state={{ companyId: idCurrentCompany, companyName: companyInfo.name }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <CardDescriptionSection>
                    <span style={{ fontWeight: 'bold', color: '#1F2124', fontSize: 20 }}>
                      Homologación de competencias
                    </span>
                  </CardDescriptionSection>
                </Link>
              </Card>
            </CardContainer>
            <LogoContainer>
              <Circle>
                <img
                  src={companyInfo.logo}
                  style={{ width: 100, height: 100, objectFit: 'contain' }}
                  alt={`Logo de ${companyInfo.name}`}
                />
              </Circle>
              {isEditing && (
                <div
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <label
                    htmlFor="photo"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginTop: '8px',
                    }}
                    title="Cambiar imagen"
                  >
                    <span style={{ color: '#00A6FF', whiteSpace: 'nowrap' }}>Cambiar imagen</span>
                    <LoopIcon style={{ color: '#00A6FF', marginLeft: '5px' }} />
                    <input
                      style={{
                        opacity: 0,
                        position: 'absolute',
                        width: 200,
                        height: 70,
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                      }}
                      type="file"
                      id="photo"
                      name="photo"
                      accept="image/png, image/jpeg"
                      onChange={(e) => handleImage(e)}
                      value=""
                    />
                  </label>
                </div>
              )}
            </LogoContainer>
            <CompanyCard>
              <Formik
                validateOnChange={true}
                validateOnMount={true}
                validationSchema={SignUpSchema}
                onSubmit={(values) => {
                  onSubmit({
                    name: values.name,
                    typeCompanyId: parseInt(values.typeCompanyId),
                    operativeId: parseInt(values.operativeId),
                    contactName: values.contactName,
                    contactEmail: values.contactEmail,
                    phone: values.phone,
                    commercialInCharge: values.commercialInCharge,
                    companyCategory: parseInt(values.companyCategory),
                    companyCharacter: companyCharacter || '',
                    logo: newImageBlob ? newImageBlob : undefined,
                    hasIntegration: values.hasIntegration,
                    integrationType: values.integrationType ?? undefined,
                    industryType: values.industryType,
                    usesAi: values.usesAi,
                  })
                }}
                initialValues={{
                  name: companyInfo.name,
                  typeCompanyId: companyInfo.idCharacter.toString(),
                  operativeId: companyInfo.idOperativo.toString(),
                  contactName: companyInfo.contact_name,
                  contactEmail: companyInfo.contact_email,
                  phone: companyInfo.phone,
                  commercialInCharge: companyInfo.commercial_name || '',
                  companyCategory: companyInfo.category_id?.toString() || '',
                  categoryName: companyInfo.categoryName || '',
                  companyCharacter: companyInfo.character,
                  hasIntegration: companyInfo.allow_integration.toString(),
                  integrationType: companyInfo.integrationType ?? undefined,
                  industryType: companyInfo.industryType || '',
                  usesAi: companyInfo.usesAi || false,
                }}
              >
                {({ errors, touched, values, setFieldValue, handleChange, handleBlur, handleSubmit, handleReset }) => {
                  return (
                    <Form style={{ width: '100%' }}>
                      <Block display="flex" flexDirection="column">
                        <Row width="100%" gap="20px" margin="0px 0px 20px 0px">
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Input
                              name="name"
                              type="text"
                              label="Nombre de la empresa"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.name}
                              disabled={!isEditing}
                            />
                            {errors.name && touched.name ? (
                              <ErrorContainer>
                                <TextError>{errors.name}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Select
                              width="98%"
                              hideNeverChoice={true}
                              label="Operativo asociado"
                              options={associatedOperating}
                              onChange={(e) => {
                                handleChange('operativeId')(e.toString())
                              }}
                              value={values.operativeId}
                              disabled={!isEditing}
                            />
                            {errors.operativeId && touched.operativeId ? (
                              <ErrorContainer>
                                <TextError>{errors.operativeId}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Input
                              name="contactName"
                              type="text"
                              label="Nombre de contacto"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.contactName}
                              disabled={!isEditing}
                            />
                            {errors.contactName && touched.contactName ? (
                              <ErrorContainer>
                                <TextError>{errors.contactName}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                        </Row>
                        <Row width="100%" gap="20px" margin="0px 0px 20px 0px">
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Input
                              name="contactEmail"
                              type="text"
                              label="Email de contacto"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.contactEmail}
                              disabled={!isEditing}
                            />
                            {errors.contactEmail && touched.contactEmail ? (
                              <ErrorContainer>
                                <TextError>{errors.contactEmail}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Input
                              name="phone"
                              type="number"
                              label="Teléfono de contacto"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.phone}
                              disabled={!isEditing}
                            />
                            {errors.phone && touched.phone ? (
                              <ErrorContainer>
                                <TextError>{errors.phone}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row width="48%" flexDirection="column" alignItems="left">
                            <Input
                              name="commercialInCharge"
                              type="text"
                              label="Comercial encargado"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.commercialInCharge}
                              disabled={!isEditing}
                            />
                            {errors.commercialInCharge && touched.commercialInCharge ? (
                              <ErrorContainer>
                                <TextError>{errors.commercialInCharge}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                        </Row>
                        <Row flexDirection="column" alignItems="left" margin="0px 0px 20px 0px">
                          <Select
                            width="32%"
                            hideNeverChoice={true}
                            label="Tipo de industria de la compañía"
                            options={industryTypes}
                            onChange={(e) => {
                              handleChange('industryType')(e.toString())
                            }}
                            value={values.industryType}
                            disabled={!isEditing}
                          />
                          {errors.industryType && touched.industryType ? (
                            <ErrorContainer>
                              <TextError>{errors.industryType}</TextError>
                            </ErrorContainer>
                          ) : null}
                        </Row>
                        <Row
                          width="100%"
                          alignItems="center"
                          justifyContent="left"
                          gap="15px"
                          margin="10px 0px 20px 10px"
                        >
                          <P fontSize="18px" weight={600} color="#1F2124">
                            Inteligencia Artificial
                          </P>
                          <Switch
                            checked={values.usesAi}
                            onChange={(e) => {
                              setFieldValue('usesAi', e.target.checked)
                              setAiActive(e.target.checked)
                              if (e.target.checked === false) {
                                setAddMoreInfo(false)
                              }
                            }}
                            aria-label="Con IA"
                            disabled={!isEditing}
                          />
                        </Row>
                        <Row
                          width="100%"
                          flexDirection={screenWidth < 1240 ? 'column' : 'row'}
                          justifyContent="space-between"
                          padding="0px 30px 0px 10px"
                          alignItems="left"
                        >
                          <CheckboxContainer>
                            <H4 color="#1F2124" margin="0px 10px 10px 0px">
                              Categoría:
                            </H4>
                            <Row justify-content="flex-start" width="auto">
                              <RadioCheck
                                label="Demo"
                                onChange={() => {
                                  setCompanyCategory('Demo')
                                  setFieldValue('companyCategory', '1')
                                  setFieldValue('categoryName', 'Demo')
                                }}
                                value={values.categoryName}
                                checked={companyCategory === 'Demo'}
                                disabled={!isEditing}
                              />

                              <Row justify-content="flex-start" width="auto">
                                <RadioCheck
                                  label="Suscripción"
                                  onChange={() => {
                                    setCompanyCategory('Suscripción')
                                    setFieldValue('companyCategory', '2')
                                    setFieldValue('categoryName', 'Suscripción')
                                  }}
                                  value={values.categoryName}
                                  checked={companyCategory === 'Suscripción'}
                                  disabled={!isEditing}
                                />
                              </Row>
                              <Row justify-content="flex-start" width="auto">
                                <RadioCheck
                                  label="Proyecto"
                                  onChange={() => {
                                    setCompanyCategory('Proyecto')
                                    setFieldValue('companyCategory', '3')
                                    setFieldValue('categoryName', 'Proyecto')
                                  }}
                                  value={values.categoryName}
                                  checked={companyCategory === 'Proyecto'}
                                  disabled={!isEditing}
                                />
                              </Row>
                            </Row>
                            {errors.companyCategory && touched.companyCategory ? (
                              <ErrorContainer>
                                <TextError>{errors.companyCategory}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </CheckboxContainer>
                          <CheckboxContainer>
                            <H4 color="#1F2124" margin="0px 10px 10px 0px">
                              ¿Cuál es el carácter de esta empresa?
                            </H4>
                            <Row justifyContent="flex-start" width="auto">
                              <RadioCheck
                                label="Corporativo"
                                onChange={() => {
                                  setCompanyCharacter('Corporativo')
                                  handleChange('typeCompanyId')('1')
                                  handleChange('companyCharacter')('Corporativo')
                                }}
                                value={values.typeCompanyId}
                                checked={values.typeCompanyId === '1'}
                                disabled={!isEditing}
                              />
                              <RadioCheck
                                label="Vocacional"
                                onChange={() => {
                                  setCompanyCharacter('Vocacional')
                                  handleChange('typeCompanyId')('2')
                                  handleChange('companyCharacter')('Vocacional')
                                }}
                                value={values.typeCompanyId}
                                checked={values.typeCompanyId === '2'}
                                disabled={!isEditing}
                              />
                            </Row>
                            {errors.typeCompanyId && touched.typeCompanyId ? (
                              <ErrorContainer>
                                <TextError>{errors.typeCompanyId}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </CheckboxContainer>
                          <CheckboxContainer>
                            <H4 color="#1F2124" margin="0px 10px 10px 0px">
                              ¿Tiene integración?
                            </H4>
                            <Row justifyContent="flex-start" width="auto">
                              <RadioCheck
                                label="Si"
                                onChange={() => {
                                  setHasIntegration('true')
                                  handleChange('hasIntegration')('true')
                                }}
                                value={values.hasIntegration}
                                checked={values.hasIntegration === 'true'}
                                disabled={!isEditing}
                              />

                              <Row justify-content=" flex-start" width="auto">
                                <RadioCheck
                                  label="No"
                                  onChange={() => {
                                    setHasIntegration('false')
                                    handleChange('hasIntegration')('false')
                                  }}
                                  value={values.hasIntegration}
                                  checked={values.hasIntegration === 'false'}
                                  disabled={!isEditing}
                                />
                              </Row>
                            </Row>
                          </CheckboxContainer>
                        </Row>
                        <Row width="100%">
                          {values.hasIntegration === 'true' && (
                            <CheckboxContainer style={{ marginLeft: '10px' }}>
                              <H3 color="#1F2124" margin="0px 10px 10px 0px">
                                Tipo de integración
                              </H3>
                              <Row justify-content="flex-start" width="auto">
                                {integrations?.length &&
                                  integrations?.map((item, index) => (
                                    <RadioCheck
                                      key={`integration-${index}`}
                                      label={item.name}
                                      onChange={() => {
                                        setFieldValue('integrationType', item.id)
                                      }}
                                      value={item.id.toString()}
                                      checked={values.integrationType === item.id}
                                      disabled={!isEditing}
                                    />
                                  ))}
                              </Row>
                              {errors.integrationType && touched.integrationType ? (
                                <ErrorContainer>
                                  <TextError>{errors.integrationType}</TextError>
                                </ErrorContainer>
                              ) : null}
                            </CheckboxContainer>
                          )}
                        </Row>
                      </Block>
                      {isEditing && (
                        <LastButtonsContainer>
                          <StrokedButton
                            onClick={() => {
                              handleReset()
                              handleEdit()
                              setAddMoreInfo(false)
                              setAiActive(companyInfo.usesAi || false)
                              setCompanyInfo({ ...companyInfo, logo: imageCompany })
                            }}
                            style={{ borderRadius: '10px', margin: '0' }}
                            color="#2D749C"
                          >
                            Cancelar
                          </StrokedButton>
                          <Button disabled={isDisabled(errors)} type="submit" onClick={handleSubmit}>
                            Guardar
                          </Button>
                        </LastButtonsContainer>
                      )}
                    </Form>
                  )
                }}
              </Formik>
              {!isEditing && (
                <ButtonEdit onClick={handleEdit}>
                  <IconSvgProfile
                    clipRule="evenodd"
                    fillRule="evenodd"
                    strokeLinejoin="round"
                    strokeMiterlimit="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m19 20.25c0-.402-.356-.75-.75-.75-2.561 0-11.939 0-14.5 0-.394 0-.75.348-.75.75s.356.75.75.75h14.5c.394 0 .75-.348.75-.75zm-7.403-3.398 9.124-9.125c.171-.171.279-.423.279-.684 0-.229-.083-.466-.28-.662l-3.115-3.104c-.185-.185-.429-.277-.672-.277s-.486.092-.672.277l-9.143 9.103c-.569 1.763-1.555 4.823-1.626 5.081-.02.075-.029.15-.029.224 0 .461.349.848.765.848.511 0 .991-.189 5.369-1.681zm-3.27-3.342 2.137 2.137-3.168 1.046zm.955-1.166 7.651-7.616 2.335 2.327-7.637 7.638z"
                      fillRule="nonzero"
                    />
                  </IconSvgProfile>
                  <label style={{ cursor: 'pointer' }}>Editar compañía</label>
                </ButtonEdit>
              )}
            </CompanyCard>
            <Row margin="20px 0 0 20px">
              <H2>Características organizacionales</H2>
              <TooltipComponent
                tooltipText="Esta información es requerida si la compañía tiene activa la IA"
                placement="top"
              />
              {validateMoreInfo() && (
                <ButtonEdit onClick={handleAddMoreInfo} style={{ margin: '0' }}>
                  <IconSvgProfile
                    clipRule="evenodd"
                    fillRule="evenodd"
                    strokeLinejoin="round"
                    strokeMiterlimit="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m19 20.25c0-.402-.356-.75-.75-.75-2.561 0-11.939 0-14.5 0-.394 0-.75.348-.75.75s.356.75.75.75h14.5c.394 0 .75-.348.75-.75zm-7.403-3.398 9.124-9.125c.171-.171.279-.423.279-.684 0-.229-.083-.466-.28-.662l-3.115-3.104c-.185-.185-.429-.277-.672-.277s-.486.092-.672.277l-9.143 9.103c-.569 1.763-1.555 4.823-1.626 5.081-.02.075-.029.15-.029.224 0 .461.349.848.765.848.511 0 .991-.189 5.369-1.681zm-3.27-3.342 2.137 2.137-3.168 1.046zm.955-1.166 7.651-7.616 2.335 2.327-7.637 7.638z"
                      fillRule="nonzero"
                    />
                  </IconSvgProfile>
                  <label style={{ cursor: 'pointer' }}>Agregar características</label>
                </ButtonEdit>
              )}
            </Row>
            <InfoWrapper>
              <InfoContainer addGap={addMoreInfo}>
                <H4>Momento del ciclo de vida de la empresa:</H4>
                <H4>Nivel de madurez del área de talento humano:</H4>
                <H4>Complejidad del mercado:</H4>
                <H4>Modelo de trabajo:</H4>
              </InfoContainer>
              <InfoContainer style={{ marginTop: '5px', width: '40%' }}>
                {!addMoreInfo ? (
                  <>
                    <P color="#1F2124" fontSize="18px">
                      {companyInfo.lifeCycle}
                    </P>
                    <P color="#1F2124" fontSize="18px">
                      {companyInfo.hrMaturityLevel}
                    </P>
                    <P color="#1F2124" fontSize="18px">
                      {companyInfo.marketComplexity}
                    </P>
                    <P color="#1F2124" fontSize="18px">
                      {companyInfo.workModel}
                    </P>
                  </>
                ) : (
                  <Formik
                    validateOnChange={true}
                    validateOnMount={true}
                    validationSchema={MoreInfoSchema}
                    onSubmit={(values) => {
                      handleSubmitAddMoreInfo({
                        workModel: values.workModel,
                        marketComplexity: values.marketComplexity,
                        lifeCycle: values.lifeCycle,
                        hrMaturityLevel: values.maturityLevel,
                      })
                    }}
                    initialValues={{
                      workModel: companyInfo.workModel,
                      marketComplexity: companyInfo.marketComplexity,
                      lifeCycle: companyInfo.lifeCycle,
                      maturityLevel: companyInfo.hrMaturityLevel,
                    }}
                  >
                    {({ errors, touched, values, handleChange, handleBlur, handleSubmit }) => {
                      return (
                        <Form style={{ width: '100%' }}>
                          <Row flexDirection="column" margin="0 0 0 0">
                            <Select
                              hideNeverChoice={true}
                              label="Ciclo de vida de la empresa"
                              options={lifeCycles}
                              onChange={(e) => {
                                handleChange('lifeCycle')(e.toString())
                              }}
                              value={values.lifeCycle}
                            />
                            {errors.lifeCycle && touched.lifeCycle ? (
                              <ErrorContainer>
                                <TextError>{errors.lifeCycle}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row flexDirection="column" margin="20px 0 20px 0">
                            <Select
                              width="100%"
                              hideNeverChoice={true}
                              label="Madurez del área de talento humano"
                              options={maturityLevels}
                              onChange={(e) => {
                                handleChange('maturityLevel')(e.toString())
                              }}
                              value={values.maturityLevel}
                            />
                            {errors.maturityLevel && touched.maturityLevel ? (
                              <ErrorContainer>
                                <TextError>{errors.maturityLevel}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Row flexDirection="column" margin="0 0 30px 0">
                            <Select
                              hideNeverChoice={true}
                              label="Complejidad del mercado"
                              options={marketComplexity}
                              onChange={(e) => {
                                handleChange('marketComplexity')(e.toString())
                              }}
                              value={values.marketComplexity}
                            />
                            {errors.marketComplexity && touched.marketComplexity ? (
                              <ErrorContainer>
                                <TextError>{errors.marketComplexity}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>

                          <Row flexDirection="column" margin="20px 0 0 0">
                            <Input
                              name="workModel"
                              type="textarea"
                              label="Modelo de trabajo"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.workModel}
                              style={{ height: '133px', width: '100%' }}
                              minRows={6}
                              maxRows={5}
                            />
                            {errors.workModel && touched.workModel ? (
                              <ErrorContainer>
                                <TextError>{errors.workModel}</TextError>
                              </ErrorContainer>
                            ) : null}
                          </Row>
                          <Button
                            disabled={isDisabled(errors)}
                            type="submit"
                            onClick={handleSubmit}
                            style={{ marginTop: '20px' }}
                          >
                            Guardar
                          </Button>
                        </Form>
                      )
                    }}
                  </Formik>
                )}
              </InfoContainer>
            </InfoWrapper>
          </>
        ) : (
          <ModalLoader isLoading={loading} />
        )}
      </div>
    </MainLayout>
  )
}

export default Company
